/**
 * Usernames: the author's public, unique handle. It is the byline everywhere and
 * the author page's address (`/u/<username>`). English letters and digits only,
 * so it is safe in a URL as-is; unique regardless of case (the database enforces
 * that with a unique index on lower(username)). Accounts that never chose one get
 * `author<digits>`.
 */

export const MIN_USERNAME_LENGTH = 3;
export const MAX_USERNAME_LENGTH = 24;

const USERNAME_PATTERN = new RegExp(`^[A-Za-z0-9]{${MIN_USERNAME_LENGTH},${MAX_USERNAME_LENGTH}}$`);

/**
 * Names nobody may take: they would impersonate the service or collide with an
 * owner id the author page also resolves (`system`), or read as the bare
 * generated default. Compared case-insensitively.
 */
export const RESERVED_USERNAMES: readonly string[] = [
  "admin",
  "administrator",
  "author",
  "lorvok",
  "lorevok",
  "moderator",
  "mod",
  "root",
  "staff",
  "support",
  "system",
  "official",
  "help"
];

export type UsernameProblem = "invalid" | "reserved";

/** Why `raw` can't be a username (format or reserved), or null when it can. Uniqueness is the store's job. */
export function usernameProblem(raw: unknown): UsernameProblem | null {
  if (typeof raw !== "string" || !USERNAME_PATTERN.test(raw)) return "invalid";
  return RESERVED_USERNAMES.includes(raw.toLowerCase()) ? "reserved" : null;
}

export function isValidUsername(raw: unknown): raw is string {
  return usernameProblem(raw) === null;
}

/** Trims surrounding whitespace; the rest is checked by {@link usernameProblem}. */
export function normalizeUsername(raw: unknown): string {
  return typeof raw === "string" ? raw.trim() : "";
}
